import { History, SportsSoccer, Square } from '@mui/icons-material';
import {
  Box,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from '@mui/material';
import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import theme from '../../styles/theme';
import { capitalizar, CATEGORIAS_ORDEN } from '../../utils/UtilesComun';
import { obtenerColorCategoria, obtenerColorNota, obtenerPorcentajeTitular } from '../../utils/UtilesJugador';
import { BoxTitleApp, PaperApp, TypographyApp } from '../custom/CustomComponents';

const SEPARADOR_TEMPORADA = '2.5px solid #94a3b8';

function truncarCategoria(texto) {
  if (!texto) {
    return '';
  }
  let textoNuevo = texto.replace(/FUTBOL SALA/gi, 'FS');
  textoNuevo = textoNuevo.replace(/FÚTBOL SALA/gi, 'FS');
  textoNuevo = textoNuevo.replace(/COMARCALES/gi, 'C.');
  return textoNuevo;
}

/**
 * Tabla de historico de temporadas de un jugador modernizada
 */
const HistoricoJugador = ({ historicoJugador, temporadaActual, mostrarActual, title, loading = false }) => {
  const navigate = useNavigate();

  const historicoTemporadas = useMemo(() => {
    if (!historicoJugador || !Array.isArray(historicoJugador)) {
      return [];
    }

    let filas = historicoJugador;
    if (mostrarActual === true) {
      filas = historicoJugador.filter((fila) => fila.temporada === temporadaActual);
    } else if (mostrarActual === false) {
      filas = historicoJugador.filter((fila) => fila.temporada !== temporadaActual);
    }

    const getTeamLetterScore = (nombre) => {
      if (!nombre) {
        return 1.5;
      }
      const parts = nombre.trim().split(/\s+/);
      const lastPart = parts[parts.length - 1];
      if (/^['"]?[a-zA-Z]{1,2}['"]?$/.test(lastPart)) {
        const l = lastPart.replace(/[^a-zA-Z]/g, '').toUpperCase();
        if (l === 'A') {
          return 1;
        }
        return l.charCodeAt(0) - 'A'.charCodeAt(0) + 1;
      }
      return 1.5;
    };

    return [...filas].sort((a, b) => {
      // 1. Temporada descendente
      const tempComp = (b.temporada || '').localeCompare(a.temporada || '');
      if (tempComp !== 0) {
        return tempComp;
      }

      // 2. Misma temporada: primero la categoría según CATEGORIAS_ORDEN
      const catA = a.categoria?.toUpperCase() || 'OTROS';
      const catB = b.categoria?.toUpperCase() || 'OTROS';
      const indexA = CATEGORIAS_ORDEN.indexOf(catA);
      const indexB = CATEGORIAS_ORDEN.indexOf(catB);
      if (indexA !== -1 && indexB !== -1 && indexA !== indexB) {
        return indexA - indexB;
      }
      if (indexA !== -1 && indexB === -1) {
        return -1;
      }
      if (indexA === -1 && indexB !== -1) {
        return 1;
      }

      // 3. Dentro de la categoría: letra de equipo (A < sin letra < B < C...)
      const scoreA = getTeamLetterScore(a.nombreEquipo);
      const scoreB = getTeamLetterScore(b.nombreEquipo);
      if (scoreA !== scoreB) {
        return scoreA - scoreB;
      }

      // 4. Si tienen la misma letra pero nombres base diferentes
      const nombreComp = (a.nombreEquipo || '').localeCompare(b.nombreEquipo || '');
      if (nombreComp !== 0 && a.idEquipo !== b.idEquipo) {
        return nombreComp;
      }

      // 5. Mismo equipo: mayor número de partidos convocados (PC)
      const convA = Number(a.convocatorias ?? 0);
      const convB = Number(b.convocatorias ?? 0);
      if (convB !== convA) {
        return convB - convA;
      }

      // 6. En caso de empate en convocatorias: minutos
      const minA = Number(a.minutos ?? 0);
      const minB = Number(b.minutos ?? 0);
      if (minB !== minA) {
        return minB - minA;
      }

      return (a.division || '').localeCompare(b.division || '');
    });
  }, [historicoJugador, mostrarActual, temporadaActual]);

  const tituloTabla = useMemo(() => {
    if (title) {
      return title;
    }
    if (mostrarActual === true) {
      return 'Temporada Actual';
    }
    if (mostrarActual === false) {
      return 'Histórico Temporadas';
    }
    return 'Histórico de Temporadas';
  }, [title, mostrarActual]);

  if ((!historicoJugador || historicoJugador.length === 0) && !loading) {
    return null;
  }

  return (
    <PaperApp sx={{ p: 0, overflow: 'hidden', border: `0.5px solid ${theme.palette.primary.main}` }}>
      <BoxTitleApp
        conFondo
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '0.9rem',
          gap: 1,
        }}
      >
        <History sx={{ fontSize: '1.2rem', color: theme.palette.primary.main }} />
        <span style={{ fontWeight: 600 }}>{tituloTabla}</span>
      </BoxTitleApp>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200, py: 6 }}>
          <CircularProgress size={32} sx={{ color: theme.palette.primary.main }} />
        </Box>
      ) : (
        <TableContainer sx={{ width: '100%', overflowX: 'auto' }}>
          <Table size="small" sx={{ width: '100%', tableLayout: 'fixed' }}>
            <TableHead>
              <TableRow
                sx={{
                  backgroundColor: '#f8fafc',
                  '& th': {
                    fontWeight: 700,
                    fontSize: '0.75rem',
                    py: 0.75,
                    px: 0.5,
                    color: 'text.secondary',
                    borderBottom: `1px solid ${theme.palette.divider}`,
                  },
                  '& th:first-of-type': { pl: '28px !important' },
                }}
              >
                <TableCell sx={{ width: '11%', pl: '28px !important', pr: 0.5, py: 0.75 }}>Temporada</TableCell>
                <TableCell sx={{ width: '10%' }}>Categoría</TableCell>
                <TableCell sx={{ width: '21.5%' }}>Equipo</TableCell>
                <TableCell sx={{ width: '19.5%' }}>Liga</TableCell>
                <TableCell sx={{ width: '6%', textAlign: 'center' }}>
                  <Tooltip title="Nota Media de la temporada" arrow>
                    <span>Nota</span>
                  </Tooltip>
                </TableCell>
                <TableCell sx={{ width: '4.5%', textAlign: 'center' }}>
                  <Tooltip title="Partidos Convocado" arrow>
                    <span>PC</span>
                  </Tooltip>
                </TableCell>
                <TableCell sx={{ width: '4.5%', textAlign: 'center' }}>
                  <Tooltip title="Titularidades" arrow>
                    <span>PT</span>
                  </Tooltip>
                </TableCell>
                <TableCell sx={{ width: '5%', textAlign: 'center' }}>
                  <Tooltip title="Porcentaje de titularidades" arrow>
                    <span>%T</span>
                  </Tooltip>
                </TableCell>
                <TableCell sx={{ width: '6%', textAlign: 'center' }}>
                  <Tooltip title="Minutos jugados" arrow>
                    <span>Min</span>
                  </Tooltip>
                </TableCell>
                <TableCell sx={{ width: '4%', textAlign: 'center' }}>
                  <Tooltip title="Goles" arrow>
                    <SportsSoccer sx={{ fontSize: '0.88rem', color: '#15803d', verticalAlign: 'middle' }} />
                  </Tooltip>
                </TableCell>
                <TableCell sx={{ width: '4%', textAlign: 'center' }}>
                  <Tooltip title="Tarjetas amarillas" arrow>
                    <Square sx={{ fontSize: '0.80rem', color: '#eab308', verticalAlign: 'middle' }} />
                  </Tooltip>
                </TableCell>
                <TableCell sx={{ width: '4%', textAlign: 'center' }}>
                  <Tooltip title="Tarjetas rojas" arrow>
                    <Square sx={{ fontSize: '0.80rem', color: '#ef4444', verticalAlign: 'middle' }} />
                  </Tooltip>
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {historicoTemporadas.map((registro, index) => {
                const esNuevaTemporada = index > 0 && historicoTemporadas[index - 1].temporada !== registro.temporada;
                const mostrarTextoTemporada =
                  index === 0 || historicoTemporadas[index - 1].temporada !== registro.temporada;
                const porcentajeTitular = obtenerPorcentajeTitular(registro.convocatorias, registro.titularidades);
                const totalGoles = (registro.goles || 0) + (registro.golesPenalti || 0);

                return (
                  <TableRow
                    key={`${registro.nombreEquipo},${registro.temporada},${registro.categoria},${registro.grupo},${index}`}
                    hover
                    sx={{
                      '&:hover': { backgroundColor: '#f8fafc' },
                      '& td:first-of-type': { pl: '28px !important' },
                      ...(esNuevaTemporada && {
                        '& > td': {
                          borderTop: `${SEPARADOR_TEMPORADA} !important`,
                        },
                      }),
                    }}
                  >
                    {/* Temporada */}
                    <TableCell sx={{ pl: '28px !important', pr: 0.5, py: 0.6 }}>
                      {mostrarTextoTemporada ? (
                        <TypographyApp sx={{ fontWeight: 700, fontSize: '0.80rem', color: 'text.primary' }}>
                          {registro.temporada}
                        </TypographyApp>
                      ) : null}
                    </TableCell>

                    {/* Categoría con badge moderno */}
                    <TableCell sx={{ py: 0.6, px: 0.5 }}>
                      <Chip
                        label={capitalizar(truncarCategoria(registro.categoria))}
                        size="small"
                        sx={{
                          backgroundColor: obtenerColorCategoria(registro.categoria) || '#f1f5f9',
                          color: '#0f172a',
                          fontWeight: 600,
                          fontSize: '0.72rem',
                          height: 22,
                          borderRadius: '4px',
                          border: '1px solid rgba(0, 0, 0, 0.08)',
                          '& .MuiChip-label': { px: 0.6 },
                        }}
                      />
                    </TableCell>

                    {/* Equipo */}
                    <TableCell sx={{ py: 0.6, px: 0.5, overflow: 'hidden' }}>
                      <Tooltip title={capitalizar(registro.nombreEquipo)} arrow>
                        <Box
                          onClick={() => navigate(`/equipo/${registro.idEquipo}`)}
                          sx={{
                            cursor: 'pointer',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            '&:hover': { textDecoration: 'underline' },
                          }}
                        >
                          <TypographyApp
                            sx={{
                              fontSize: '0.80rem',
                              fontWeight: 600,
                              color: 'primary.dark',
                              '&:hover': { color: 'primary.main' },
                            }}
                            handleOverflow
                          >
                            {capitalizar(registro.nombreEquipo)}
                          </TypographyApp>
                        </Box>
                      </Tooltip>
                    </TableCell>

                    {/* Liga / División */}
                    <TableCell sx={{ py: 0.6, px: 0.5, overflow: 'hidden' }}>
                      <Tooltip title={capitalizar(registro.division)} arrow>
                        <Box
                          onClick={registro.idLiga ? () => navigate(`/liga/${registro.idLiga}`) : undefined}
                          sx={{
                            cursor: registro.idLiga ? 'pointer' : 'default',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            '&:hover': registro.idLiga ? { textDecoration: 'underline' } : {},
                          }}
                        >
                          <TypographyApp
                            sx={{
                              fontSize: '0.80rem',
                              color: registro.idLiga ? 'primary.dark' : 'text.secondary',
                              fontWeight: registro.idLiga ? 600 : 400,
                              '&:hover': registro.idLiga ? { color: 'primary.main' } : {},
                            }}
                            handleOverflow
                          >
                            {capitalizar(registro.division)}
                          </TypographyApp>
                        </Box>
                      </Tooltip>
                    </TableCell>

                    {/* Nota Media */}
                    <TableCell sx={{ textAlign: 'center', py: 0.6, px: 0.25 }}>
                      {registro.notaMediaTemporada !== null && registro.notaMediaTemporada !== undefined ? (
                        <Chip
                          size="small"
                          label={Number(registro.notaMediaTemporada).toFixed(1)}
                          sx={{
                            backgroundColor: obtenerColorNota(registro.notaMediaTemporada).bg,
                            color: obtenerColorNota(registro.notaMediaTemporada).color,
                            border: `1px solid ${obtenerColorNota(registro.notaMediaTemporada).border}`,
                            fontWeight: 700,
                            fontSize: '0.78rem',
                            height: 22,
                            width: 44,
                            minWidth: 44,
                            maxWidth: 44,
                            borderRadius: '4px',
                            '& .MuiChip-label': { px: 0.4 },
                          }}
                        />
                      ) : (
                        <span style={{ color: '#94a3b8' }}>-</span>
                      )}
                    </TableCell>

                    {/* PC (Convocatorias) */}
                    <TableCell sx={{ textAlign: 'center', py: 0.6, px: 0.25 }}>
                      <TypographyApp sx={{ fontSize: '0.80rem', fontWeight: 600 }}>
                        {registro.convocatorias ?? 0}
                      </TypographyApp>
                    </TableCell>

                    {/* PT (Titularidades) */}
                    <TableCell sx={{ textAlign: 'center', py: 0.6, px: 0.25 }}>
                      <TypographyApp sx={{ fontSize: '0.80rem', fontWeight: 500, color: 'text.secondary' }}>
                        {registro.titularidades ?? 0}
                      </TypographyApp>
                    </TableCell>

                    {/* %T */}
                    <TableCell sx={{ textAlign: 'center', py: 0.6, px: 0.25 }}>
                      <TypographyApp sx={{ fontSize: '0.78rem', fontWeight: 500, color: 'text.secondary' }}>
                        {porcentajeTitular}
                      </TypographyApp>
                    </TableCell>

                    {/* Minutos */}
                    <TableCell sx={{ textAlign: 'center', py: 0.6, px: 0.25 }}>
                      <TypographyApp sx={{ fontSize: '0.80rem', fontWeight: 500 }}>
                        {registro.minutos ? `${registro.minutos}'` : '-'}
                      </TypographyApp>
                    </TableCell>

                    {/* Goles */}
                    <TableCell sx={{ textAlign: 'center', py: 0.6, px: 0.25 }}>
                      {totalGoles > 0 ? (
                        <TypographyApp sx={{ fontSize: '0.80rem', fontWeight: 700, color: '#15803d' }}>
                          {totalGoles}
                        </TypographyApp>
                      ) : (
                        <span style={{ color: '#94a3b8' }}>-</span>
                      )}
                    </TableCell>

                    {/* Amarillas */}
                    <TableCell sx={{ textAlign: 'center', py: 0.6, px: 0.25 }}>
                      {registro.amarillas > 0 ? (
                        <Box sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 0.25 }}>
                          <Square sx={{ fontSize: '0.70rem', color: '#eab308' }} />
                          <TypographyApp sx={{ fontSize: '0.78rem', fontWeight: 600, color: '#a16207' }}>
                            {registro.amarillas}
                          </TypographyApp>
                        </Box>
                      ) : (
                        <span style={{ color: '#94a3b8' }}>-</span>
                      )}
                    </TableCell>

                    {/* Rojas */}
                    <TableCell sx={{ textAlign: 'center', py: 0.6, px: 0.25 }}>
                      {registro.rojas > 0 ? (
                        <Box sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 0.25 }}>
                          <Square sx={{ fontSize: '0.70rem', color: '#ef4444' }} />
                          <TypographyApp sx={{ fontSize: '0.78rem', fontWeight: 600, color: '#b91c1c' }}>
                            {registro.rojas}
                          </TypographyApp>
                        </Box>
                      ) : (
                        <span style={{ color: '#94a3b8' }}>-</span>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </PaperApp>
  );
};

HistoricoJugador.propTypes = {
  historicoJugador: PropTypes.array,
  temporadaActual: PropTypes.string,
  mostrarActual: PropTypes.bool,
  title: PropTypes.string,
  loading: PropTypes.bool,
};

export default HistoricoJugador;
